import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement
} from '@stripe/react-stripe-js'
import { Field, ErrorMessage, useFormikContext } from 'formik'

import { FormValues } from '.'
import { theme } from '@tailwind.config'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'
import styles from '@components/UI/Form/Input/Input.module.css'
import { InvalidMessage } from '@components/UI/Form/InvalidMessage'

export function PaymentInfo() {
  const {
    touched,
    errors,
    setFieldError,
    setFieldTouched
  } = useFormikContext<FormValues>()

  return (
    <div className="block bg-white p-8 rounded shadow-sm" id="billing">
      <h2 className="text-2xl font-black tracking-tight mb-8">
        Payment Information
      </h2>
      <h3 className="text-l font-black mb-4">Billing Address</h3>
      <div className="grid md:grid-cols-2 gap-4 md:gap-x-8 md:gap-y-4 mb-8">
        <div>
          <Label required htmlFor="billing.street">
            Street Address
          </Label>
          <Field
            component={Input}
            name="billing.street"
            placeholder="1234 N Main St"
            validate={(street: string) => {
              if (!street) return 'Please provide a street address'
            }}
          />
        </div>
        <div>
          <Label required htmlFor="billing.city">
            City
          </Label>
          <Field
            component={Input}
            name="billing.city"
            placeholder="City"
            validate={(city: string) => {
              if (!city) return 'Please provide a city'
            }}
          />
        </div>
        <div>
          <Label required htmlFor="billing.state">
            State
          </Label>
          <Field
            component={Input}
            name="billing.state"
            placeholder="ST"
            maxLength={2}
            className="uppercase"
            validate={(state: string) => {
              if (state.length < 2) return 'Please provide a valid state'
            }}
          />
        </div>
        <div>
          <Label required htmlFor="billing.zipCode">
            Zip Code
          </Label>
          <Field
            component={Input}
            name="billing.zipCode"
            maxLength={5}
            placeholder="12345"
            validate={(zipCode: string) => {
              if (!zipCode.match(/^\d{5}$/)) {
                return 'Please provide a valid zip code'
              }
            }}
          />
        </div>
      </div>
      <h3 className="text-l font-black mb-4">Card Details</h3>
      <div className="grid md:grid-cols-2 gap-4 md:gap-x-8 md:gap-y-4">
        <div className="col-span-2">
          <Label required>Card Number</Label>
          <Field
            name="billing.cardNumber"
            className="hidden"
            validate={() => {
              if (errors.billing?.cardNumber) return errors.billing.cardNumber
              else if (!touched.billing?.cardNumber) {
                return 'Please provide a card number'
              }
            }}
          />
          <CardNumberElement
            className={`${styles.input} ${
              errors.billing?.cardNumber && touched.billing?.cardNumber
                ? styles.error
                : ''
            }`}
            onChange={event => {
              if (event.error) {
                setFieldError('billing.cardNumber', event.error.message)
              } else if (event.complete && !event.error) {
                setFieldTouched('billing.cardNumber')
                setFieldError('billing.cardNumber', undefined)
              }
            }}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  lineHeight: '24px',
                  color:
                    errors.billing?.cardNumber && touched.billing?.cardNumber
                      ? theme.extend.colors.red[800]
                      : theme.extend.colors.gray[700],
                  ...(errors.billing?.cardNumber && touched.billing?.cardNumber
                    ? {
                        '::placeholder': {
                          color: theme.extend.colors.red[300]
                        }
                      }
                    : {})
                },
                invalid: {
                  color: theme.extend.colors.red[800],
                  '::placeholder': {
                    color: theme.extend.colors.red[300]
                  }
                },
                empty: {
                  '::placeholder': {
                    color:
                      errors.billing?.cardNumber && touched.billing?.cardNumber
                        ? theme.extend.colors.red[300]
                        : theme.extend.colors.gray[400]
                  }
                }
              }
            }}
          />
          <ErrorMessage name="billing.cardNumber" component={InvalidMessage} />
        </div>
        <div>
          <Label required>Expiration</Label>
          <Field
            name="billing.expiry"
            className="hidden"
            validate={() => {
              if (errors.billing?.expiry) return errors.billing.expiry
              else if (!touched.billing?.expiry) {
                return 'Please provide a card expiration date'
              }
            }}
          />
          <CardExpiryElement
            className={`${styles.input} ${
              errors.billing?.expiry && touched.billing?.expiry
                ? styles.error
                : ''
            }`}
            onChange={event => {
              if (event.error) {
                setFieldError('billing.expiry', event.error.message)
              } else if (event.complete && !event.error) {
                setFieldTouched('billing.expiry')
                setFieldError('billing.expiry', undefined)
              }
            }}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  lineHeight: '24px',
                  color:
                    errors.billing?.expiry && touched.billing?.expiry
                      ? theme.extend.colors.red[800]
                      : theme.extend.colors.gray[700],
                  ...(errors.billing?.expiry && touched.billing?.expiry
                    ? {
                        '::placeholder': {
                          color: theme.extend.colors.red[300]
                        }
                      }
                    : {})
                },
                invalid: {
                  color: theme.extend.colors.red[800],
                  '::placeholder': {
                    color: theme.extend.colors.red[300]
                  }
                },
                empty: {
                  '::placeholder': {
                    color:
                      errors.billing?.expiry && touched.billing?.expiry
                        ? theme.extend.colors.red[300]
                        : theme.extend.colors.gray[400]
                  }
                }
              }
            }}
          />
          <ErrorMessage name="billing.expiry" component={InvalidMessage} />
        </div>
        <div>
          <Label required>CVC</Label>
          <Field
            name="billing.cvc"
            className="hidden"
            validate={() => {
              if (errors.billing?.cvc) return errors.billing.cvc
              else if (!touched.billing?.cvc) {
                return 'Please provide a card CVC'
              }
            }}
          />
          <CardCvcElement
            className={`${styles.input} ${
              errors.billing?.cvc && touched.billing?.cvc ? styles.error : ''
            }`}
            onChange={event => {
              if (event.error) {
                setFieldError('billing.cvc', event.error.message)
              } else if (event.complete && !event.error) {
                setFieldTouched('billing.cvc')
                setFieldError('billing.cvc', undefined)
              }
            }}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  lineHeight: '24px',
                  color:
                    errors.billing?.cvc && touched.billing?.cvc
                      ? theme.extend.colors.red[800]
                      : theme.extend.colors.gray[700],
                  ...(errors.billing?.cvc && touched.billing?.cvc
                    ? {
                        '::placeholder': {
                          color: theme.extend.colors.red[300]
                        }
                      }
                    : {})
                },
                invalid: {
                  color: theme.extend.colors.red[800],
                  '::placeholder': {
                    color: theme.extend.colors.red[300]
                  }
                },
                empty: {
                  '::placeholder': {
                    color:
                      errors.billing?.cvc && touched.billing?.cvc
                        ? theme.extend.colors.red[300]
                        : theme.extend.colors.gray[400]
                  }
                }
              }
            }}
          />
          <ErrorMessage name="billing.cvc" component={InvalidMessage} />
        </div>
      </div>
    </div>
  )
}
